/**
 * Platform intents: what Asset360 is, which agents run a module and what they
 * did overnight, what agents may do without approval, the fleet rules the
 * incidents produced, and which mines have live data.
 */

import { FLEET, MINES, MINE_ORDER } from '../../../data/core.ts';
import { FLEET_RULES } from '../../oem-engine.ts';
import { AGENTS, AGENT_BY_NAME, agentLastRunText } from '../../seed/agents.ts';
import { ALERTS } from '../../seed/alerts.ts';
import { AREAS } from '../../seed/areas.ts';
import { CONNECTORS } from '../../seed/connectors.ts';
import { MODULES, MODULE_BY_ID } from '../../seed/modules.ts';
import { loadMachineRecord, loadOemDomain } from '../../useOemData.ts';
import {
  agentLastRunContext,
  maximoMachinesNeeded,
} from '../../../modules/integration/AgentsScene.transform.ts';
import type { MaximoAsset } from '../../../schema/service.ts';
import { loadJobs, route } from '../answer-kit.ts';
import { loadWorkOrders } from '../tools.ts';
import { cues, type IntentDef } from './types.ts';

// ---------------------------------------------------------------------------
// asset360 — demo L4061
// ---------------------------------------------------------------------------

export const asset360: IntentDef = {
  id: 'asset360',
  family: 'platform',
  priority: 94,
  examples: ['What is in each Asset360 module this morning?', 'What is Asset360?'],
  score: cues({
    any: [
      /asset ?360|operating model|ten modules|10 modules|fourteen|14 capabilities|service model|capability map|people, customers and parts|each module|at each stage/,
      /(all|the) (ten |10 )?modules\b|every module/,
    ],
    not: [/this module/],
  }),
  async answer({ kit }) {
    kit.ran('operating_model');
    kit.ran('open_jobs_by_module');
    const J = await loadJobs();
    const open = MODULES.filter((m) => J.some((j) => j.stage === m.id))
      .map((m) => `**${m.short}** ${J.filter((j) => j.stage === m.id).length}`)
      .join(' · ');
    const markdown =
      `**Asset360** is the operating model: a control tower over **ten modules** — ` +
      `${MODULES.map((m) => `${m.n} ${m.short}`).join(', ')} — with **five feedback loops** ` +
      `(component lifecycle, warranty & campaigns, fleet learning, commercial opportunity, demand & supply planning) ` +
      `that feed the front end again, **${AGENTS.length} agents** and **${CONNECTORS.length} connected systems** underneath. ` +
      `The ${AREAS.length} capabilities of the original aftermarket wheel sit inside the modules as lineage. ` +
      `Open jobs this morning: ${open}. Click any module to deep-dive.`;
    return kit.done(markdown, ['Asset360', 'operating model'], { navigate: route('asset360') });
  },
};

// ---------------------------------------------------------------------------
// module_agents — new: "which agents run this module and what did they do overnight?"
// ---------------------------------------------------------------------------

/** The agents' "last run" sentences need the same inputs the Agents page loads. */
async function lastRunContext() {
  const [workOrders, parts, service] = await Promise.all([
    loadWorkOrders(),
    loadOemDomain('parts'),
    loadOemDomain('service'),
  ]);
  const ids = maximoMachinesNeeded();
  const pairs = await Promise.all(
    ids.map((id) =>
      loadMachineRecord(id).then(
        (r) => [id, r.mx] as const,
        () => [id, undefined] as const,
      ),
    ),
  );
  const maximo: Record<string, MaximoAsset | undefined> = {};
  for (const [id, mx] of pairs) if (mx) maximo[id] = mx;
  return agentLastRunContext({
    workOrders,
    orders: parts.orders,
    parts: parts.parts,
    inventory: parts.inventory,
    cases: service.cases,
    techJobs: service.tapp_jobs,
    contracts: service.mx_contracts,
    maximo,
  });
}

export const moduleAgents: IntentDef = {
  id: 'module_agents',
  family: 'platform',
  priority: 91,
  examples: ['Which agents run this module and what did they do overnight?'],
  needs: ['module'],
  score: cues({
    any: [/which agents?|agents? (run|own|work|behind)|what did (they|the agents?) do|overnight/],
    base: 5,
    bonus: (E) => (E.deictic.thisModule || E.moduleFrom === 'text' ? 1 : 0),
  }),
  async answer({ kit, E }) {
    const mod = MODULE_BY_ID[E.module!];
    kit.ran('agent_roster', { module: mod.id });
    const ctx = await lastRunContext();
    const lines = mod.agents
      .map((name) => AGENT_BY_NAME[name])
      .filter((a) => a != null)
      .map(
        (a) =>
          `- **${a.name}** (${a.level === 'L2' ? 'L2 automated' : 'L1 assisted'}) — ${a.produces}. Last run: ${agentLastRunText(a, ctx)}.`,
      );
    const markdown =
      `**${String(mod.n).padStart(2, '0')} ${mod.name}** is run by ${mod.agents.join(' and ')}.\n\n` +
      lines.join('\n') +
      `\n\nEvery output they produce is a draft until a person approves it.`;
    return kit.done(markdown, ['agents', 'work_orders.csv', 'order_history.csv'], {
      navigate: route('agents'),
    });
  },
};

// ---------------------------------------------------------------------------
// agent_autonomy — new: "what can agents do without approval at L2?"
// ---------------------------------------------------------------------------

const LEVELS: Readonly<Record<number, string>> = {
  1: 'L1 · Assisted — insights only; a person does everything',
  2: 'L2 · Automated — drafts work orders, requisitions and restrictions; a person approves each one',
  3: 'L3 · Autonomous with oversight — executes pre-approved low-risk actions (not enabled)',
  4: 'L4 · Self-optimising fleet — proposes policy changes, review by exception (not enabled)',
};

export const agentAutonomy: IntentDef = {
  id: 'agent_autonomy',
  family: 'platform',
  priority: 92,
  examples: ['What can agents do without approval at L2?', 'Which agents are at Level 1?'],
  score: cues({
    any: [/without (approval|a person|sign-?off)|autonomy|autonomous|what can (the )?agents do|agents? .*\b(level|L[1-4])\b|\blevel [1-4]\b/],
    not: [/autonomous (rigs?|drills?|loaders?|trucks?|machines?)/],
    bonus: (E) => (E.level ? 1 : 0),
    base: 6,
  }),
  async answer({ kit, E }) {
    kit.ran('agent_roster', { level: E.level ? `L${E.level}` : undefined });
    const l2 = AGENTS.filter((a) => a.level === 'L2');
    const l1 = AGENTS.filter((a) => a.level === 'L1');
    const lvl = E.level ?? 2;
    const at = AGENTS.filter((a) => a.level === `L${lvl}`);
    const markdown =
      `**At L${lvl}, ${lvl >= 3 ? 'nothing runs yet' : lvl === 2 ? 'an agent drafts — it never executes' : 'an agent only advises'}.** ` +
      (lvl === 2
        ? `Without approval an L2 agent can read, rank, reserve stock against a draft and write the draft itself — a work order, a requisition, a restriction. ` +
          `Releasing an order, spending money or restricting a machine needs a person in Approvals.`
        : lvl === 1
          ? `An L1 agent surfaces insights; a person raises every draft.`
          : `L3 and L4 are on the maturity ladder but not enabled at any site.`) +
      `\n\n- ${LEVELS[1]}\n- ${LEVELS[2]}\n- ${LEVELS[3]}\n- ${LEVELS[4]}\n\n` +
      `${at.length ? `At L${lvl} (${at.length}): ${at.map((a) => a.name).join(', ')}. ` : ''}` +
      `${l2.length} of ${AGENTS.length} agents run at L2; ${l1.map((a) => a.name).join(' and ')} stay at L1. ` +
      `Kalbarri joined the pilot at L1 on 08 Sep; Broken Ridge and Mount Isolde run L2.`;
    return kit.done(markdown, ['agents', 'maturity ladder'], { navigate: route('agents') });
  },
};

// ---------------------------------------------------------------------------
// fleet_rules — new: "which fleet rules came out of the incidents?"
// ---------------------------------------------------------------------------

export const fleetRules: IntentDef = {
  id: 'fleet_rules',
  family: 'platform',
  priority: 93,
  examples: ['Which fleet rules came out of the incidents?'],
  score: cues({
    any: [/fleet rules?|rules? (came|come) out|standing rules?|what rules|lessons? learn|what did we learn/],
    base: 6,
  }),
  async answer({ kit }) {
    kit.ran('fleet_rules');
    const markdown =
      `**${FLEET_RULES.length} fleet rules are running**, each one produced by a close-out:\n\n` +
      `| Rule | From | Applies to |\n|---|---|---|\n` +
      FLEET_RULES.map(([rule, from, applies]) => `| ${rule} | ${from} | ${applies} |`).join('\n') +
      `\n\nThe Fleet Learning agent turns an incident's precursor into a Watch rule; it runs on every machine the rule applies to, owner-operated and contractor alike.`;
    return kit.done(markdown, ['fleet rules', 'incidents'], { navigate: route('loops', 'learning') });
  },
};

// ---------------------------------------------------------------------------
// live_data — "which mines have live data and what difference does it make?"
// ---------------------------------------------------------------------------

export const liveData: IntentDef = {
  id: 'live_data',
  family: 'platform',
  priority: 88,
  examples: ['Which mines have live data and what difference does it make?'],
  score: cues({
    any: [/live data|live (feed|telematics)|shift-?end (data|export)|data modes?|which mines (are|have) live/],
    base: 5,
  }),
  async answer({ kit }) {
    kit.ran('data_modes');
    const live = MINE_ORDER.filter((m) => MINES[m].data_mode === 'Live');
    const shiftEnd = MINE_ORDER.filter((m) => MINES[m].data_mode !== 'Live');
    const liveAlerts = ALERTS.filter((a) => a.mode === 'LIVE');
    const count = (ms: readonly string[]) => FLEET.filter((f) => ms.includes(f.mine)).length;
    const markdown =
      `**${live.length} mines are live** — ${live.map((m) => `${MINES[m].name} (${MINES[m].fms.split(' — ')[0]})`).join('; ')} — ` +
      `${count(live)} machines. **${shiftEnd.length} read a shift-end export** — ${shiftEnd.map((m) => MINES[m].name).join(', ')} — ${count(shiftEnd)} machines.\n\n` +
      `The difference is when an alert can fire: ${liveAlerts.length} of the ${ALERTS.length} open alerts fired inside the shift on live data ` +
      `(${liveAlerts.map((a) => a.machine).join(', ')}); the rest waited for the morning read. ` +
      `L-02's coupling drift was visible in Kalbarri's shift-end export for two weeks — shift-end data is enough to see a slow precursor, not a fast one.`;
    return kit.done(markdown, ['connectors', 'alerts'], { navigate: route('connectors') });
  },
};
